import { Avatar, AvatarFallback, AvatarImage } from '@radix-ui/react-avatar'
import {
  Building,
  Check,
  ChevronDown,
  Globe,
  HelpCircle,
  LogOut,
  Menu,
  Settings,
  ShieldCheck,
  Store,
  User,
} from 'lucide-react'
import { forwardRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useLocation } from 'react-router-dom'

import { useAdminPageHeader } from '@/components/admin/AdminPageHeaderContext'
import { startProductTour } from '@/components/shared/ProductTour'
import { ThemeToggle } from '@/components/shared/ThemeToggle'
import type { ButtonProps } from '@/components/ui/button'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { useUploadPreSignedUrl, useUploads } from '@/hooks'
import { useAuth } from '@/hooks/useAuth'
import { changeLanguage, languages } from '@/i18n'
import { APP_ROUTES } from '@/libs/constants'
import type { Organization, Store as StoreRecord, User as UserProfile } from '@/libs/interfaces'
import { cn } from '@/libs/utils'
import { useAppDispatch, useAppSelector } from '@/store/hooks'
import { setCurrentStore } from '@/store/slices/authSlice'

import { FullPageLoader } from '../shared'

interface RouteInfo {
  title: string
  subtitle?: string
}

interface TopbarProps {
  onOpenMobileSidebar?: () => void
  variant?: 'app' | 'admin'
}

const getUserInitials = (user: UserProfile | null) => {
  const initials = `${user?.firstName?.[0] ?? ''}${user?.lastName?.[0] ?? ''}`
  return initials || 'JD'
}

const resolveOrgRoleName = (organizations: Organization[]) => organizations[0]?.roleName

const resolveActiveOrgName = (
  currentOrganization: Organization | null,
  organizations: Organization[],
) => currentOrganization?.orgName || organizations[0]?.orgName

const shouldShowAdminEntry = (isAdmin: boolean, user: UserProfile | null) =>
  !isAdmin && Boolean(user?.isSuperAdmin)

const getStoredOrganizations = (): Organization[] =>
  JSON.parse(localStorage.getItem('organizations') || '[]') as Organization[]

const resolveCurrentLanguage = (i18nLanguage: string) =>
  languages.find((lang) => lang.code === i18nLanguage) || languages[0]

const isProfileUploadQueryEnabled = (orgID: string, userID: string) =>
  Boolean(orgID) && Boolean(userID)

const resolveUserID = (user: UserProfile | null) => user?.userID ?? ''

const resolveProfileUploadID = (
  uploadsData: { uploads?: Array<{ uploadID?: string }> } | undefined,
) => uploadsData?.uploads?.[0]?.uploadID ?? ''

const resolveOrgStores = (currentOrganization: Organization | null): StoreRecord[] =>
  currentOrganization?.stores ?? []

const resolvePageInfo = (
  isAdmin: boolean,
  adminHeader: { title?: string; subtitle?: string },
  routeInfo: RouteInfo,
): RouteInfo => {
  if (!isAdmin) return routeInfo
  return {
    title: adminHeader.title ?? routeInfo.title,
    subtitle: adminHeader.subtitle ?? routeInfo.subtitle,
  }
}

interface TopbarTitleProps {
  pageInfo: RouteInfo
  onOpenMobileSidebar?: () => void
}

function TopbarTitle({ pageInfo, onOpenMobileSidebar }: TopbarTitleProps) {
  const { t } = useTranslation()

  return (
    <div className="flex items-center gap-2 min-w-0" data-tour="topbar-title">
      <button
        onClick={onOpenMobileSidebar}
        className="md:hidden h-14 w-12 shrink-0 inline-flex items-center justify-center border-r border-border text-foreground transition-colors hover:bg-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        aria-label={t('nav.expandSidebar')}
      >
        <Menu className="w-5 h-5" />
      </button>
      <div className="min-w-0 flex items-center gap-3">
        <div className="min-w-0">
          <h1 className="text-sm md:text-base font-semibold text-foreground truncate tracking-tight">
            {pageInfo.title}
          </h1>
          {pageInfo.subtitle && (
            <p className="text-[11px] md:text-xs text-muted-foreground truncate -mt-0.5">
              {pageInfo.subtitle}
            </p>
          )}
        </div>
      </div>
    </div>
  )
}

interface StoreSelectorMenuProps {
  currentStore: StoreRecord | null
  stores: StoreRecord[]
  onSelectStore: (store: StoreRecord) => void
}

function StoreSelectorMenu({ currentStore, stores, onSelectStore }: StoreSelectorMenuProps) {
  const { t } = useTranslation()
  const hasStores = stores.length > 0

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="outline"
              className="flex items-center gap-2 px-3 h-9"
              data-tour="topbar-store"
            >
              <Store className="w-4 h-4" />
              <span className="hidden sm:inline max-w-[120px] truncate">
                {currentStore?.storeName || t('store.mainStore')}
              </span>
              <ChevronDown className="w-4 h-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel>{t('store.stores')}</DropdownMenuLabel>
            <DropdownMenuSeparator />
            {hasStores ? (
              stores.map((store) => {
                const isSelected = currentStore?.storeID === store.storeID
                return (
                  <DropdownMenuItem
                    key={store.storeID}
                    className={cn(
                      'flex items-center justify-between cursor-pointer',
                      isSelected && 'bg-secondary',
                    )}
                    onClick={() => onSelectStore(store)}
                  >
                    <div className="flex flex-col">
                      <span className="font-medium">{store.storeName}</span>
                      <span className="text-caption">{store.storeAddress}</span>
                    </div>
                    {isSelected && <Check className="w-4 h-4 text-primary" />}
                  </DropdownMenuItem>
                )
              })
            ) : (
              <DropdownMenuItem disabled>
                <span className="text-muted-foreground">{t('store.noStores')}</span>
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </TooltipTrigger>
      <TooltipContent>{t('tooltips.topbar.store')}</TooltipContent>
    </Tooltip>
  )
}

function HowToGuideButton() {
  const { t } = useTranslation()

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          onClick={startProductTour}
          data-tour="topbar-howto"
          aria-label={t('common.help')}
        >
          <HelpCircle className="w-5 h-5" />
        </Button>
      </TooltipTrigger>
      <TooltipContent>{t('topbar.howTo')}</TooltipContent>
    </Tooltip>
  )
}

function AdminEntryLinks() {
  const { t } = useTranslation()

  return (
    <>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            asChild
            variant="outline"
            size="sm"
            className="hidden sm:inline-flex h-9 gap-1.5 border-emerald-600/40 bg-emerald-600/10 text-emerald-700 hover:bg-emerald-600/20 hover:text-emerald-800"
          >
            <Link to={APP_ROUTES.admin.base} aria-label={t('admin.entryLink', 'Admin panel')}>
              <ShieldCheck className="w-4 h-4" />
              <span>{t('admin.entryLink', 'Admin panel')}</span>
            </Link>
          </Button>
        </TooltipTrigger>
        <TooltipContent>{t('admin.entryLink', 'Admin panel')}</TooltipContent>
      </Tooltip>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button asChild variant="ghost" size="icon" className="sm:hidden text-emerald-700">
            <Link to={APP_ROUTES.admin.base} aria-label={t('admin.entryLink', 'Admin panel')}>
              <ShieldCheck className="w-5 h-5" />
            </Link>
          </Button>
        </TooltipTrigger>
        <TooltipContent>{t('admin.entryLink', 'Admin panel')}</TooltipContent>
      </Tooltip>
    </>
  )
}

interface OrganizationSwitcherSubmenuProps {
  currentOrganization: Organization | null
  organizations: Organization[]
  onSwitchOrganization: (org: Organization) => void
}

function OrganizationSwitcherSubmenu({
  currentOrganization,
  organizations,
  onSwitchOrganization,
}: OrganizationSwitcherSubmenuProps) {
  const { t } = useTranslation()
  const activeOrgName = resolveActiveOrgName(currentOrganization, organizations)

  return (
    <DropdownMenuSub>
      <DropdownMenuSubTrigger>
        <Building className="mr-2 h-4 w-4" />
        {t('org.organization')}
        <span className="ml-auto text-xs text-muted-foreground truncate max-w-[80px]">
          {activeOrgName}
        </span>
      </DropdownMenuSubTrigger>
      <DropdownMenuSubContent sideOffset={8} alignOffset={-5} className="min-w-[200px]">
        {organizations.map((org) => {
          const isSelected = currentOrganization?.orgID === org.orgID
          return (
            <DropdownMenuItem
              key={org.orgID}
              onClick={() => onSwitchOrganization(org)}
              className={cn(
                'flex items-center justify-between cursor-pointer',
                isSelected && 'bg-secondary',
              )}
            >
              <div className="flex flex-col">
                <span className="font-medium">{org.orgName}</span>
                <span className="text-xs text-muted-foreground">{org.roleName}</span>
              </div>
              {isSelected && <Check className="w-4 h-4 text-primary" />}
            </DropdownMenuItem>
          )
        })}
      </DropdownMenuSubContent>
    </DropdownMenuSub>
  )
}

interface LanguageSwitcherSubmenuProps {
  currentLanguage: (typeof languages)[number]
  onChangeLanguage: (code: string) => void
}

function LanguageSwitcherSubmenu({
  currentLanguage,
  onChangeLanguage,
}: LanguageSwitcherSubmenuProps) {
  const { t } = useTranslation()

  return (
    <DropdownMenuSub>
      <DropdownMenuSubTrigger>
        <Globe className="mr-2 h-4 w-4" />
        {t('user.language')}
        <span className="ml-auto text-xs text-muted-foreground">{currentLanguage.flag}</span>
      </DropdownMenuSubTrigger>
      <DropdownMenuSubContent sideOffset={8} alignOffset={-5}>
        {languages.map((language) => {
          const isSelected = language.code === currentLanguage.code
          return (
            <DropdownMenuItem
              key={language.code}
              onClick={() => onChangeLanguage(language.code)}
              className={isSelected ? 'bg-secondary' : ''}
            >
              <span className="mr-2">{language.flag}</span>
              {language.name}
              {isSelected && <Check className="ml-auto h-4 w-4" />}
            </DropdownMenuItem>
          )
        })}
      </DropdownMenuSubContent>
    </DropdownMenuSub>
  )
}

interface UserAvatarButtonProps extends ButtonProps {
  profilePicUrl: string | undefined
  initials: string
  firstName: string | undefined
  orgRoleName: string | undefined
}

// Radix's `asChild` (used by both TooltipTrigger and DropdownMenuTrigger above this
// component in the tree) clones its child and injects a ref plus event/aria props onto
// it. Without forwardRef and a rest-prop spread, those are silently dropped and the
// underlying button never receives the click handler that opens the dropdown.
const UserAvatarButton = forwardRef<HTMLButtonElement, UserAvatarButtonProps>(
  ({ profilePicUrl, initials, firstName, orgRoleName, className, ...props }, ref) => {
    return (
      <Button
        ref={ref}
        variant="ghost"
        className={cn('flex items-center gap-2 px-2', className)}
        data-tour="topbar-user"
        {...props}
      >
        <Avatar className="w-8 h-8 rounded-full overflow-hidden shrink-0">
          {profilePicUrl && (
            <AvatarImage
              src={profilePicUrl}
              alt={firstName ?? ''}
              className="w-8 h-8 rounded-full object-cover"
            />
          )}
          <AvatarFallback
            className={cn(
              'w-8 h-8 rounded-full flex items-center justify-center',
              'bg-primary text-primary-foreground font-semibold text-xs uppercase',
            )}
          >
            {initials}
          </AvatarFallback>
        </Avatar>
        <div className="hidden md:flex flex-col items-start">
          <span className="label-sm">{firstName}</span>
          <span className="text-caption">{orgRoleName}</span>
        </div>
      </Button>
    )
  },
)
UserAvatarButton.displayName = 'UserAvatarButton'

interface UserMenuProps {
  isAdmin: boolean
  user: UserProfile | null
  profilePicUrl: string | undefined
  organizations: Organization[]
  currentOrganization: Organization | null
  currentLanguage: (typeof languages)[number]
  onSwitchOrganization: (org: Organization) => void
  onChangeLanguage: (code: string) => void
  onLogout: () => void
}

function UserMenu({
  isAdmin,
  user,
  profilePicUrl,
  organizations,
  currentOrganization,
  currentLanguage,
  onSwitchOrganization,
  onChangeLanguage,
  onLogout,
}: UserMenuProps) {
  const { t } = useTranslation()
  const initials = getUserInitials(user)
  const orgRoleName = resolveOrgRoleName(organizations)
  const showAdminEntry = shouldShowAdminEntry(isAdmin, user)
  const showOrgSwitcher = !isAdmin && organizations.length > 0

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <UserAvatarButton
              profilePicUrl={profilePicUrl}
              initials={initials}
              firstName={user?.firstName}
              orgRoleName={orgRoleName}
            />
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel>{t('user.myAccount')}</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem asChild>
              <Link
                to={isAdmin ? APP_ROUTES.admin.account : APP_ROUTES.account}
                className="flex items-center cursor-pointer"
              >
                <User className="mr-2 h-4 w-4" />
                {t('user.profile')}
              </Link>
            </DropdownMenuItem>
            {!isAdmin && (
              <DropdownMenuItem asChild>
                <Link to={APP_ROUTES.settings.base} className="flex items-center cursor-pointer">
                  <Settings className="mr-2 h-4 w-4" />
                  {t('user.settings')}
                </Link>
              </DropdownMenuItem>
            )}
            {showAdminEntry && (
              <DropdownMenuItem asChild>
                <Link to={APP_ROUTES.admin.base} className="flex items-center cursor-pointer">
                  <ShieldCheck className="mr-2 h-4 w-4" />
                  {t('admin.entryLink', 'Admin panel')}
                </Link>
              </DropdownMenuItem>
            )}
            {/* Organization Switcher */}
            {showOrgSwitcher && (
              <OrganizationSwitcherSubmenu
                currentOrganization={currentOrganization}
                organizations={organizations}
                onSwitchOrganization={onSwitchOrganization}
              />
            )}
            {/* Language Switcher */}
            {!isAdmin && (
              <LanguageSwitcherSubmenu
                currentLanguage={currentLanguage}
                onChangeLanguage={onChangeLanguage}
              />
            )}
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={onLogout} className="text-destructive cursor-pointer">
              <LogOut className="mr-2 h-4 w-4" />
              {t('auth.logout')}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </TooltipTrigger>
      <TooltipContent>{t('tooltips.topbar.userMenu')}</TooltipContent>
    </Tooltip>
  )
}

export function AppTopbar({ onOpenMobileSidebar, variant = 'app' }: TopbarProps) {
  const location = useLocation()
  const { t, i18n } = useTranslation()
  const { user, currentOrganization, currentStore } = useAppSelector((state) => state.auth)
  const { logout, switchOrganization, isLoggingOut } = useAuth()
  const dispatch = useAppDispatch()
  const adminHeader = useAdminPageHeader()
  const isAdmin = variant === 'admin'

  const orgID = useAppSelector((state) => state?.auth?.currentOrganization?.orgID ?? '')

  const organizations = getStoredOrganizations()
  const currentLanguage = resolveCurrentLanguage(i18n.language)

  const userID = resolveUserID(user)

  const { data: uploadsData } = useUploads({
    params: { orgID, userID, profilePic: true, limit: 1 },
    enabled: isProfileUploadQueryEnabled(orgID, userID),
  })
  const profileUploadID = resolveProfileUploadID(uploadsData)

  const { data: profilePicUrl } = useUploadPreSignedUrl({
    params: { orgID, uploadID: profileUploadID },
    enabled: Boolean(profileUploadID),
  })

  const routeMap: Record<string, RouteInfo> = {
    [APP_ROUTES.home]: {
      title: t('nav.dashboard'),
      subtitle: t('topbar.dashboardSubtitle'),
    },
    // [APP_ROUTES.boo]: { title: t('nav.scheduler'), subtitle: t('topbar.schedulerSubtitle') },
    [APP_ROUTES.booking.base]: {
      title: t('nav.booking'),
      subtitle: t('topbar.bookingSubtitle'),
    },
    [APP_ROUTES.booking.new()]: {
      title: t('booking.newBooking'),
      subtitle: t('topbar.newBookingSubtitle'),
    },
    [APP_ROUTES.customer.base]: {
      title: t('nav.customers'),
      subtitle: t('topbar.customersSubtitle'),
    },
    [APP_ROUTES.customer.new]: {
      title: t('customers.addCustomer'),
      subtitle: t('topbar.newCustomerSubtitle'),
    },
    // [APP_ROUTES.customer.newCompany]: {
    //   title: t('customers.addCompany'),
    //   subtitle: t('topbar.newCompanySubtitle'),
    // },
    [APP_ROUTES.vehicle.base]: {
      title: t('nav.vehicles'),
      subtitle: t('topbar.vehiclesSubtitle'),
    },
    [APP_ROUTES.vehicle.new]: {
      title: t('vehicles.addVehicle'),
      subtitle: t('topbar.newVehicleSubtitle'),
    },
    [APP_ROUTES.settings.service.base]: {
      title: t('nav.services'),
      subtitle: t('topbar.servicesSubtitle'),
    },
    [APP_ROUTES.settings.service.new]: {
      title: t('services.addService'),
      subtitle: t('topbar.newServiceSubtitle'),
    },
    [APP_ROUTES.job.base]: {
      title: t('nav.jobs'),
      subtitle: t('topbar.jobsSubtitle'),
    },
    [APP_ROUTES.pricing]: {
      title: t('nav.pricing'),
      subtitle: t('topbar.pricingSubtitle'),
    },
    [APP_ROUTES.roles]: {
      title: t('nav.roles'),
      subtitle: t('topbar.rolesSubtitle'),
    },
    [APP_ROUTES.settings.base]: {
      title: t('nav.settings'),
      subtitle: t('topbar.settingsSubtitle'),
    },
    [APP_ROUTES.account]: {
      title: t('nav.account'),
      subtitle: t('topbar.accountSubtitle'),
    },
    [APP_ROUTES.changePassword]: { title: t('nav.changePassword') },
    [APP_ROUTES.leaves]: {
      title: t('nav.leaves'),
      subtitle: t('topbar.leavesSubtitle'),
    },
    [APP_ROUTES.workLogs]: {
      title: t('nav.workLogs'),
      subtitle: t('topbar.workLogsSubtitle'),
    },
    // [APP_ROUTES.invoicing.base]: { title: t('nav.billing'), subtitle: t('topbar.billingSubtitle') },
    [APP_ROUTES.product.base]: {
      title: t('nav.products'),
      subtitle: t('topbar.productsSubtitle'),
    },
    [APP_ROUTES.product.new]: {
      title: t('products.addProduct'),
      subtitle: t('topbar.newProductSubtitle'),
    },
    [APP_ROUTES.rentCar.base]: {
      title: t('nav.rentalFleet'),
      subtitle: t('topbar.rentalFleetSubtitle'),
    },
    [APP_ROUTES.rentCar.new]: {
      title: t('rentalFleet.addVehicle'),
      subtitle: t('rentalFleet.addVehicleDesc'),
    },
    [APP_ROUTES.campaigns]: {
      title: t('nav.campaigns'),
      subtitle: t('topbar.campaignsSubtitle'),
    },
    [APP_ROUTES.integrations]: {
      title: t('nav.integrations'),
      subtitle: t('topbar.integrationsSubtitle'),
    },
    // Admin routes
    [APP_ROUTES.admin.base]: {
      title: t('admin.pages.overview.title', 'Admin dashboard'),
      subtitle: t('admin.pages.overview.subtitle', 'Platform-wide health at a glance'),
    },
    [APP_ROUTES.admin.organizations]: {
      title: t('admin.pages.orgs.title', 'Organizations'),
      subtitle: t('admin.pages.orgs.subtitle', 'Manage tenant organizations'),
    },
    [APP_ROUTES.admin.newOrganization]: {
      title: t('admin.pages.newOrg.title', 'New organization'),
      subtitle: t('admin.pages.newOrg.subtitle', 'Create a new tenant'),
    },
    [APP_ROUTES.admin.subscriptions]: {
      title: t('admin.pages.subs.title', 'Subscriptions'),
      subtitle: t('admin.pages.subs.subtitle', 'Billing plans and status'),
    },
    [APP_ROUTES.admin.newSubscription]: {
      title: t('admin.pages.newSub.title', 'New subscription'),
    },
    [APP_ROUTES.admin.plans]: {
      title: t('admin.pages.plans.title', 'Plans'),
      subtitle: t('admin.pages.plans.subtitle', 'Subscription plan catalog'),
    },
    [APP_ROUTES.admin.newPlan]: {
      title: t('admin.pages.newPlan.title', 'New plan'),
    },
    [APP_ROUTES.admin.features]: {
      title: t('admin.pages.features.title', 'Features'),
      subtitle: t('admin.pages.features.subtitle', 'Feature catalog'),
    },
    [APP_ROUTES.admin.newFeature]: {
      title: t('admin.pages.newFeature.title', 'New feature'),
    },
    [APP_ROUTES.admin.usage]: {
      title: t('admin.pages.usage.title', 'Usage'),
      subtitle: t('admin.pages.usage.subtitle', 'Consumption across organizations'),
    },
    [APP_ROUTES.admin.discounts]: {
      title: t('admin.pages.discounts.title', 'Discounts'),
      subtitle: t('admin.pages.discounts.subtitle', 'Promotions and price adjustments'),
    },
    [APP_ROUTES.admin.newDiscount]: {
      title: t('admin.pages.newDiscount.title', 'New discount'),
    },
    [APP_ROUTES.admin.globalRoles]: {
      title: t('admin.pages.roles.title', 'Global roles'),
      subtitle: t('admin.pages.roles.subtitle', 'Platform-wide role definitions'),
    },
    [APP_ROUTES.admin.newGlobalRole]: {
      title: t('admin.pages.newRole.title', 'New global role'),
    },
  }

  const getPageInfo = (): RouteInfo => {
    if (routeMap[location.pathname]) {
      return routeMap[location.pathname]
    }
    const pathSegments = location.pathname.split('/').filter(Boolean)
    while (pathSegments.length > 0) {
      const parentPath = '/' + pathSegments.join('/')
      if (routeMap[parentPath]) {
        return routeMap[parentPath]
      }
      pathSegments.pop()
    }
    return {
      title: t('nav.dashboard'),
      subtitle: t('topbar.dashboardSubtitle'),
    }
  }

  const routeInfo = getPageInfo()
  const pageInfo = resolvePageInfo(isAdmin, adminHeader, routeInfo)

  const showAdminEntry = shouldShowAdminEntry(isAdmin, user)

  return (
    <TooltipProvider delayDuration={300}>
      {isLoggingOut && <FullPageLoader />}
      <header className="h-14 md:h-16 bg-card border-b border-border shadow-sm flex items-center justify-between pr-3 md:px-5 md:sticky md:top-0 md:z-50 shrink-0">
        {/* Page Title with subtitle */}
        <TopbarTitle pageInfo={pageInfo} onOpenMobileSidebar={onOpenMobileSidebar} />

        {/* Topbar Actions */}
        <div className="flex items-center gap-3">
          {/* Store Selector — app only */}
          {!isAdmin && (
            <StoreSelectorMenu
              currentStore={currentStore}
              stores={resolveOrgStores(currentOrganization)}
              onSelectStore={(store) => dispatch(setCurrentStore(store))}
            />
          )}

          {/* How To Guide — app only */}
          {!isAdmin && <HowToGuideButton />}

          {/* Theme Toggle */}
          <ThemeToggle />

          {/* Back to Admin (superadmin only, from app) */}
          {showAdminEntry && <AdminEntryLinks />}

          {/* User Menu */}
          <UserMenu
            isAdmin={isAdmin}
            user={user}
            profilePicUrl={profilePicUrl}
            organizations={organizations}
            currentOrganization={currentOrganization}
            currentLanguage={currentLanguage}
            onSwitchOrganization={switchOrganization}
            onChangeLanguage={changeLanguage}
            onLogout={() => {
              void logout()
            }}
          />
        </div>
      </header>
    </TooltipProvider>
  )
}
